import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { glossary, glossaryTerms, getTranslatedEntry } from "@/lib/glossary";

/**
 * Global, DOM-level glossary auto-linker. Scans body text after every route
 * change (and on subsequent DOM mutations) and wraps occurrences of registered
 * glossary terms in an <abbr> element with a native title tooltip plus a
 * dotted underline matching the inline <Term> styling. The element links to
 * the full glossary entry.
 *
 * Why DOM-based instead of React-tree-based: terms appear inside translated
 * strings, lazy-loaded route content, third-party rendered output, etc. A
 * single mount-point cannot React.Children-walk the rendered route output.
 */
const SKIP_TAGS = new Set([
  "A", "BUTTON", "CODE", "PRE", "ABBR", "SCRIPT", "STYLE", "TEXTAREA",
  "INPUT", "SELECT", "OPTION", "LABEL", "NAV", "SVG", "NOSCRIPT", "H1",
]);

const buildPattern = () => {
  if (glossaryTerms.length === 0) return null;
  const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const alt = glossaryTerms
    .map((term) => {
      const left = /^\w/.test(term) ? "(?<![\\w-])" : "";
      const right = /\w$/.test(term) ? "(?![\\w-])" : "";
      return `${left}${escape(term)}${right}`;
    })
    .join("|");
  return new RegExp(`(${alt})`, "g");
};

const isSkippable = (node: Node | null): boolean => {
  let cur: Node | null = node;
  while (cur) {
    if (cur.nodeType === Node.ELEMENT_NODE) {
      const el = cur as HTMLElement;
      if (SKIP_TAGS.has(el.tagName)) return true;
      if (el.dataset && el.dataset.noGlossary !== undefined) return true;
      if (el.dataset && el.dataset.glossaryTerm !== undefined) return true;
    }
    cur = cur.parentNode;
  }
  return false;
};

const decorate = (root: HTMLElement, pattern: RegExp) => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const text = node.nodeValue;
      if (!text || !text.trim()) return NodeFilter.FILTER_REJECT;
      if (isSkippable(node.parentNode)) return NodeFilter.FILTER_REJECT;
      pattern.lastIndex = 0;
      return pattern.test(text) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
    },
  });

  const targets: Text[] = [];
  let n: Node | null;
  while ((n = walker.nextNode())) targets.push(n as Text);

    for (const textNode of targets) {
    // The node may have been detached by React between the TreeWalker pass
    // and this mutation. Skip silently if so.
    if (!textNode.parentNode || !textNode.isConnected) continue;
    const text = textNode.nodeValue ?? "";
    pattern.lastIndex = 0;
    const frag = document.createDocumentFragment();
    let lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = pattern.exec(text))) {
      const match = m[0];
      const entry = glossary[match];
      if (!entry) continue;
      if (m.index > lastIndex) {
        frag.appendChild(document.createTextNode(text.slice(lastIndex, m.index)));
      }
      const a = document.createElement("a");
      a.dataset.glossaryTerm = match;
      a.href = `/resources/glossary#${match.toLowerCase()}`;
      a.textContent = match;
      a.className =
        "underline decoration-dotted decoration-muted-foreground/60 underline-offset-[3px] cursor-help hover:decoration-primary hover:text-foreground transition-colors";
      frag.appendChild(a);
      lastIndex = m.index + match.length;
    }
    if (lastIndex === 0) continue;
    if (lastIndex < text.length) {
      frag.appendChild(document.createTextNode(text.slice(lastIndex)));
    }
    try {
      textNode.parentNode?.replaceChild(frag, textNode);
    } catch {
      // React reconciled this subtree before we could decorate it.
    }
  }
};

export const GlossaryAutoScan = () => {
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();
  const [active, setActive] = useState<{ key: string; rect: DOMRect } | null>(null);
  const hideTimer = useRef<number | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);

  const cancelHide = () => {
    if (hideTimer.current !== null) {
      window.clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  };
  const scheduleHide = () => {
    cancelHide();
    hideTimer.current = window.setTimeout(() => setActive(null), 120);
  };

  useEffect(() => {
    const pattern = buildPattern();
    if (!pattern) return;

    let scheduled = false;
    let decorating = false;
    const run = () => {
      scheduled = false;
      decorating = true;
      try {
        decorate(document.body, pattern);
      } finally {
        setTimeout(() => {
          decorating = false;
        }, 0);
      }
    };
    const schedule = () => {
      if (scheduled) return;
      scheduled = true;
      // Let React finish committing first, then decorate.
      requestAnimationFrame(() => setTimeout(run, 0));
    };

    schedule();

    const observer = new MutationObserver((mutations) => {
      if (decorating) return;
      for (const m of mutations) {
        if (m.addedNodes.length > 0) {
          schedule();
          return;
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [pathname, i18n.language]);

  // Global hover handlers — single shared card for every decorated term.
  useEffect(() => {
    const onOver = (e: Event) => {
      const target = (e.target as HTMLElement | null)?.closest?.(
        "[data-glossary-term]",
      ) as HTMLElement | null;
      if (!target) return;
      const key = target.dataset.glossaryTerm;
      if (!key || !glossary[key]) return;
      cancelHide();
      setActive({ key, rect: target.getBoundingClientRect() });
    };
    const onOut = (e: Event) => {
      const target = (e.target as HTMLElement | null)?.closest?.(
        "[data-glossary-term]",
      );
      if (!target) return;
      scheduleHide();
    };
    document.addEventListener("mouseover", onOver);
    document.addEventListener("mouseout", onOut);
    document.addEventListener("focusin", onOver);
    document.addEventListener("focusout", onOut);
    return () => {
      document.removeEventListener("mouseover", onOver);
      document.removeEventListener("mouseout", onOut);
      document.removeEventListener("focusin", onOver);
      document.removeEventListener("focusout", onOut);
    };
  }, []);

  if (!active) return null;
  const entry = getTranslatedEntry(active.key);
  if (!entry) return null;

  const CARD_WIDTH = 288;
  const margin = 8;
  const left = Math.min(
    Math.max(margin, active.rect.left),
    window.innerWidth - CARD_WIDTH - margin,
  );
  const top = active.rect.bottom + 6;

  return (
    <div
      ref={cardRef}
      role="tooltip"
      onMouseEnter={cancelHide}
      onMouseLeave={scheduleHide}
      style={{ position: "fixed", top, left, width: CARD_WIDTH, zIndex: 1000 }}
      className="rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none animate-in fade-in-0 zoom-in-95"
    >
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">
        {t("glossaryPage.title", { defaultValue: "Glossary" })}
      </p>
      <p className="mt-1 text-sm font-medium tracking-tight text-foreground">
        {entry.title}
      </p>
      <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
        {entry.description}
      </p>
      <Link
        to={`/resources/glossary#${active.key.toLowerCase()}`}
        className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-primary hover:text-foreground"
        onClick={() => setActive(null)}
      >
        {t("glossaryPage.viewInGlossary", { defaultValue: "View in glossary" })}{" "}
        <ArrowUpRight className="h-3 w-3" />
      </Link>
    </div>
  );
};

export default GlossaryAutoScan;